import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
  AgencyAdvisorMemberRow,
  AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';

interface UseAgencyAdvisorMembersResult {
  data: AgencyAdvisorMemberRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Il periodo è controllato dal componente padre (condiviso con la tab Panoramica).
 */
export function useAgencyAdvisorMembers(
  period: AgencyDashboardPeriod
): UseAgencyAdvisorMembersResult {
  const [data, setData] = useState<AgencyAdvisorMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await AgencyDashboardService.getAdvisorMembers(period);
      setData(result);
    } catch {
      const msg = String(t('errors.loadFailed'));
      setError(msg);
      toast({
        title: String(t('common.error')),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

export default useAgencyAdvisorMembers;
